import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from './firebase-config';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { adminServices } from './services/adminServices';
import { languageManifest as baseLanguageManifest } from './locales';
import { useManagedLanguages } from './hooks/useManagedLanguages';
import AccreditationBadge from './AccreditationBadge';
import { localizationService } from './services/localizationService';
import ArchivedReportView from './components/ArchivedReportView';
import { MagnifyIcon } from './components/AccessibilityControls';
import { setPublishExecutiveSummary } from './services/schoolPublicProfile';
import { stripInstitutionalMaturityFromExecutiveSummary } from './utils/executiveSummaryDisplay';
import { formatSchoolFacingDate, tsToDate } from './utils/formatSchoolFacingDate';

const HUB_UI_EN = {
  hub_title: "My Accreditation Reports",
  hub_subtitle: "Access your certification status, formal audit reports, and feedback archive.",
  label_current_status: "Current Status",
  status_published_title: "Cycle Complete",
  status_published_desc: "Your formal report was issued on",
  status_factcheck_title: "Action Required: Fact-Check",
  status_factcheck_desc: "Please review the draft report below and provide accuracy feedback.",
  status_review_title: "Under IFIP Review",
  status_review_desc: "Your submission is currently being reviewed by the IFIP team.",
  status_new_cycle_title: "New cycle open",
  status_new_cycle_desc: "Your previous report stays in Issued reports. Update your self-evaluation, then submit it when you are ready.",
  btn_download: "Download Certificate",
  btn_view: "View Final Report",
  header_archive: "Previous Reports",
  header_issued_reports: "Issued reports",
  label_issued: "Issued",
  label_date_issued: "Date issued",
  label_date_not_available: "—",
  hub_notice_accept_ok: "Thank you — your acceptance has been recorded.",
  hub_notice_feedback_ok: "Feedback submitted. IFIP will review your notes.",
  hub_notice_accept_err: "Could not record acceptance.",
  hub_notice_feedback_err: "Submit failed.",
  hub_notice_cert_ready: "Your certificate is ready. In the opened tab, use the download button to save it.",
  level_foundation: "Foundation",
  level_certified: "Certified",
  level_global: "Global Accreditation",
  btn_view_archive: "View",
  btn_pdf_archive: "Download PDF",
  btn_pdf_coming_soon: "PDF download will be available in a future update.",
  archive_row_aria_open: "Open issued report",
  btn_dismiss: "Dismiss",
  msg_no_archive: "No formal reports have been issued for this cycle yet.",
  badge_header: "My Digital Badge",
  badge_desc: "Select your preferred badge style, then copy the code to the clipboard.",
  badge_expiry_notice: "Note: renewal due on",
  badge_embed_code_label: "Digital badge embed code",
  btn_copy_code: "Copy Embed Code",
  msg_copied: "Embed code copied to clipboard!",
  msg_pending_badge: "When your report is published, your digital badge code will be available here.",
  badge_variant_medallion: "Medallion",
  badge_variant_banner: "Banner",
  badge_variant_seal: "Seal",
  workspace_title: "Fact-Check Workspace",
  workspace_desc: "Please review each station and provide specific feedback only if factual corrections are required.",
  report_language_label: "Report language",
  report_translating_status: "Translating report...",
  report_language_policy_notice:
    "All official IFIP reports are written in English, to avoid confusion caused by AI translation. You may translate to your own language but please note that the English version is the official version. You may type your comments in any language.",
  workspace_under_ifip_review: "Report is now with IFIP to review factual accuracy.",
  btn_submit_feedback: "Submit Accuracy Feedback",
  btn_accept: "Accept Findings",
  btn_accept_sent: "Sent to IFIP for finalisation",
  workspace_locked_note: "Your acceptance has been sent to IFIP. This workspace is now locked while finalisation is prepared.",
  report_text_zoom_group: "Report text size",
  report_text_zoom_smaller: "Decrease report text",
  report_text_zoom_larger: "Increase report text",
  header_draft_report: "Clinical Draft Report",
  label_exec_summary: "Portrait of {{schoolName}}",
  label_station: "Station",
  header_accuracy_feedback: "Accuracy Feedback",
  label_general_notes: "General Factual Notes",
  label_station_note: "Correction/Note",
  placeholder_general: "Provide overall accuracy notes here...",
  localizing_indicator: "Localizing Hub",
  placeholder_station: "Notes regarding findings...",
  btn_report_translation: "🚩 Report Translation Error",
  loading_records: "Syncing Accreditation Records...",
  benchmark_title: "Global benchmarking",
  benchmark_placeholder: "IFIP is aggregating anonymised maturity data across the network. Comparative charts will unlock once enough accredited schools have published into the global sample.",
  benchmark_progress: "Current published sample: {count} / threshold {threshold}.",
  benchmark_ready: "Your institution vs global mean ({global}): you are {delta} points relative to the average for {level} institutions (sample {count}).",
  overlay_close: "Close",
  overlay_title: "Issued report (read-only)",
  exec_summary_public_title: "Portrait of {{schoolName}}",
  exec_summary_public_intro:
    "If you agree (by clicking below this section), this portrait will be displayed publicly on the IFIP website and will be visible to viewers who click on your accreditation badge if you include it on your school website.",
  exec_summary_public_empty: "No portrait is available on your latest issued report.",
  exec_summary_publish_label: "I agree to publish this portrait as described above.",
  exec_summary_publish_saved: "Publishing preference saved.",
  exec_summary_publish_saving: "Saving…",
  exec_summary_publish_error: "Could not save publishing preference.",
  exec_summary_preview_verify: "Preview public badge page",
};

const BADGE_PUBLIC_ORIGIN = 'https://accreditation.ifip.group';

const portraitSchoolLabel = (template, schoolName) =>
  String(template || '').replace(/\{\{schoolName\}\}/g, schoolName || '');

const buildBadgeEmbedCode = (badgeId, variant, lang) => {
  const imgStyle =
    variant === 'medallion'
      ? 'width: 220px; max-width: 100%; height: auto;'
      : 'max-width: 300px; height: auto;';
  return `<a href="${BADGE_PUBLIC_ORIGIN}/verify/${badgeId}" target="_blank" rel="noopener noreferrer">
  <!-- IFIP Digital Badge: ${variant} -->
  <img src="${BADGE_PUBLIC_ORIGIN}/api/badges/${badgeId}/${variant}?lang=${encodeURIComponent(lang || 'en')}" alt="IFIP Accredited School" style="${imgStyle}" />
</a>`;
};

/** Registration uses `accredited`; report editor / benchmarks use `global`. */
const normalizeAccreditationTier = (raw) => {
  const k = (raw == null ? '' : String(raw)).toLowerCase();
  if (k === 'accredited') return 'global';
  return k;
};

const tierLabelFromUi = (ui, raw) => {
  const nk = normalizeAccreditationTier(raw);
  const label = ui[`level_${nk}`];
  if (label) return label;
  if (raw == null || raw === '') return '';
  const s = String(raw);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const badgeTierForComponent = (raw) => {
  const nk = normalizeAccreditationTier(raw || 'foundation');
  return ['foundation', 'certified', 'global'].includes(nk) ? nk : 'foundation';
};

/** Archive doc id pattern from `finalizeAccreditation`: `{level}_{month}_{year}` */
const parseArchiveIdParts = (archiveId) => {
  const m = String(archiveId || '').match(/^([a-z]+)_(\d{1,2})_(\d{4})$/i);
  if (!m) return null;
  const month = parseInt(m[2], 10);
  const year = parseInt(m[3], 10);
  if (month < 1 || month > 12) return null;
  return { levelKey: m[1].toLowerCase(), month, year };
};

const formatIssuedReportRowTitle = (item, ui, langKey, schoolProfile) => {
  const parts = parseArchiveIdParts(item.id);
  const storedLevel = item.accreditationLevel || item.meta?.accreditationLevel;
  if (parts) {
    const monthName = new Date(parts.year, parts.month - 1, 1).toLocaleDateString(langKey, { month: 'long' });
    const levelLabel = tierLabelFromUi(ui, storedLevel || parts.levelKey);
    return `${levelLabel} - ${monthName} ${parts.year}`;
  }
  const d = tsToDate(item.meta?.publishedAt || item.archivedAt);
  const levelLabel = tierLabelFromUi(ui, storedLevel || schoolProfile?.accreditationLevel || 'foundation');
  if (d) {
    const monthName = d.toLocaleDateString(langKey, { month: 'long' });
    return `${levelLabel} - ${monthName} ${d.getFullYear()}`;
  }
  return item.snapshotLabel || ui.header_issued_reports;
};

const formatIssueDateLine = (item, ui, langKey) => {
  const formatted = formatSchoolFacingDate(item.meta?.publishedAt || item.archivedAt, langKey);
  if (!formatted) return `${ui.label_date_issued} ${ui.label_date_not_available}`;
  return `${ui.label_date_issued} ${formatted}`;
};

const clampFactCheckTextScale = (v) => {
  const n = typeof v === 'number' && !Number.isNaN(v) ? v : 1;
  return Math.min(1.35, Math.max(0.75, Math.round(n * 100) / 100));
};

const resolveLangKey = (raw, manifest = baseLanguageManifest) => {
  const key = String(raw || '').trim().toLowerCase();
  if (manifest[key]) return key;
  const match = Object.entries(manifest).find(([, meta]) =>
    [meta.label, meta.nativeName].some((v) => String(v || '').trim().toLowerCase() === key)
  );
  return match?.[0] || 'en';
};

const AccreditationHub = ({
  schoolId,
  schoolProfile,
  activeLang: _activeLang,
  langKey,
  registerTranslating,
  onSchoolProfilePatch,
}) => {
  const { languageManifest } = useManagedLanguages();
  const [report, setReport] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translatedReport, setTranslatedReport] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [audit, setAudit] = useState(null);
  const [distilledData, setDistilledData] = useState(null);
  const [selectedArchiveReport, setSelectedArchiveReport] = useState(null);
  const [benchmarkReadiness, setBenchmarkReadiness] = useState(null);
  const [hubNotice, setHubNotice] = useState(null);
  const [acceptLockedLocally, setAcceptLockedLocally] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [certBusyId, setCertBusyId] = useState(null);
  const [reportLangKey, setReportLangKey] = useState('en');
  const [isReportTranslating, setIsReportTranslating] = useState(false);
  const [factCheckReportTextScale, setFactCheckReportTextScale] = useState(1);

  const refreshArchive = useCallback(() => {
    adminServices.getReportArchive(schoolId).then(setArchive).catch(console.error);
  }, [schoolId]);

  useEffect(() => {
    const reportRef = doc(db, "reports", schoolId);
    const unsubReport = onSnapshot(reportRef, (snap) => {
      if (snap.exists()) setReport(snap.data());
      else setReport(null);
      setLoading(false);
    });

    refreshArchive();

    return () => unsubReport();
  }, [schoolId, refreshArchive]);

  useEffect(() => {
    const auditRef = doc(db, "audits", schoolId);
    return onSnapshot(auditRef, (snap) => {
      if (snap.exists()) setAudit(snap.data());
      else setAudit(null);
    });
  }, [schoolId]);

  useEffect(() => {
    const distilledRef = doc(db, "distilledData", schoolId);
    return onSnapshot(distilledRef, (snap) => {
      if (snap.exists()) setDistilledData(snap.data());
      else setDistilledData(null);
    });
  }, [schoolId]);

  useEffect(() => {
    refreshArchive();
  }, [schoolId, schoolProfile?.workflowStep, schoolProfile?.finalReportIssuedAt, refreshArchive]);

  const latestArchive = archive[0];
  const isFinalised = schoolProfile?.workflowStep === 'finalised';
  const legacyLiveFinal =
    !!(report?.meta?.isPublished && Object.keys(report?.stationAnalysis || {}).length > 0);
  const hasAnyIssuedReport = archive.length > 0 || legacyLiveFinal;
  const hasCurrentCycleFinal = isFinalised && hasAnyIssuedReport;
  const latestIssuedReport = latestArchive || (legacyLiveFinal ? report : null);
  const badgePublicId = latestIssuedReport?.meta?.publicBadgeId || schoolProfile?.publicBadgeId || null;
  const canShowBadgeCode = !!latestIssuedReport && !!badgePublicId;

  useEffect(() => {
    if (!hasAnyIssuedReport) {
      setBenchmarkReadiness(null);
      return;
    }
    let cancelled = false;
    adminServices.getBenchmarkReadiness().then((r) => {
      if (!cancelled) setBenchmarkReadiness(r);
    });
    return () => { cancelled = true; };
  }, [hasAnyIssuedReport, schoolProfile?.finalReportIssuedAt]);

  const publishedAtSource = () =>
    latestIssuedReport?.meta?.publishedAt ||
    schoolProfile?.finalReportIssuedAt ||
    null;

  const handleAccept = async () => {
    if (acceptLockedLocally || schoolProfile?.workflowStep === 'accepted') return;
    if (!window.confirm("By accepting, you confirm the report is factually accurate. The final certificate will then be prepared. Proceed?")) return;
    setAcceptLockedLocally(true);
    try {
      await adminServices.acceptReportDraft(schoolId, schoolProfile.officialName);
      setHubNotice({ type: 'ok', messageKey: 'hub_notice_accept_ok' });
    } catch (e) {
      setAcceptLockedLocally(false);
      setHubNotice({ type: 'err', text: e.message || HUB_UI_EN.hub_notice_accept_err });
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!window.confirm("Submit these accuracy notes to the IFIP team? Your audit status will move to 'Appeals' while we review.")) return;
    setIsSubmittingFeedback(true);
    try {
      await adminServices.submitFactCheckFeedback(schoolId, feedback, schoolProfile.officialName);
      setHubNotice({ type: 'ok', messageKey: 'hub_notice_feedback_ok' });
    } catch (e) {
      setHubNotice({ type: 'err', text: e.message || HUB_UI_EN.hub_notice_feedback_err });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (!['fact_check', 'accepted', 'appeals'].includes(schoolProfile?.workflowStep || '')) {
      setAcceptLockedLocally(false);
    }
  }, [schoolProfile?.workflowStep]);

  useEffect(() => {
    const release = registerTranslating?.();
    const syncHubContent = async () => {
      setTranslatedReport((prev) => ({
        ui: langKey === 'en' ? HUB_UI_EN : (prev?.ui || HUB_UI_EN),
        executiveSummary: report?.institutionalBrief?.executiveSummary || "",
        stationContent: Object.keys(report?.stationAnalysis || {}).reduce((acc, sId) => {
          acc[sId] = report.stationAnalysis[sId].content;
          return acc;
        }, {})
      }));

      try {
        const uiChunk = await localizationService.sync(langKey, 'hub_main', HUB_UI_EN, 'v2_master_en', 'v2_translations');
        await localizationService.sync(langKey, 'reg_flow_v2', {});

        if (langKey !== 'en') {
          setTranslatedReport((prev) => ({ ...prev, ui: uiChunk }));
        }

        if (report) {
          const contentLang = resolveLangKey(reportLangKey);
          const shouldTranslateReport = contentLang !== 'en';
          setIsReportTranslating(shouldTranslateReport);
          const summaryDocId = `report_${schoolId}_summary`;
          const summarySource = { exec: report.institutionalBrief?.executiveSummary || "" };
          const translatedSummary = await localizationService.sync(contentLang, summaryDocId, summarySource, 'v2_report_masters', 'v2_report_translations');

          if (contentLang !== 'en') setTranslatedReport((prev) => ({ ...prev, executiveSummary: translatedSummary.exec }));

          const sIds = Object.keys(report.stationAnalysis || {});
          for (const sId of sIds) {
            const contentDocId = `report_${schoolId}_s${sId}`;
            const source = { content: report.stationAnalysis[sId].content };
            const translated = await localizationService.sync(contentLang, contentDocId, source, 'v2_report_masters', 'v2_report_translations');

            if (contentLang !== 'en') {
              setTranslatedReport((prev) => ({
                ...prev,
                stationContent: { ...(prev?.stationContent || {}), [sId]: translated.content }
              }));
            }
          }
        }
      } catch (err) {
        console.error("Hub Sync Error:", err);
      } finally {
        setIsReportTranslating(false);
        release?.();
      }
    };

    syncHubContent();
    return () => {
      release?.();
    };
  }, [langKey, reportLangKey, report, schoolId, registerTranslating]);

  const [selectedVariant, setSelectedVariant] = useState('medallion');
  const [badgeLang, setBadgeLang] = useState(langKey);
  const [publishExecSummary, setPublishExecSummary] = useState(
    () => !!schoolProfile?.publicProfile?.publishExecutiveSummary
  );
  const [publishExecSaving, setPublishExecSaving] = useState(false);
  const publishToggleInFlightRef = useRef(false);

  useEffect(() => {
    if (langKey) setBadgeLang(langKey);
  }, [langKey]);

  useEffect(() => {
    setPublishExecSummary(!!schoolProfile?.publicProfile?.publishExecutiveSummary);
  }, [schoolId]);

  useEffect(() => {
    if (publishToggleInFlightRef.current) return;
    setPublishExecSummary(!!schoolProfile?.publicProfile?.publishExecutiveSummary);
  }, [schoolProfile?.publicProfile?.publishExecutiveSummary]);

  const issuedExecutiveSummary = stripInstitutionalMaturityFromExecutiveSummary(
    latestIssuedReport?.institutionalBrief?.executiveSummary || ''
  );

  const handlePublishExecToggle = async (e) => {
    const checked = e.target.checked;
    const previous = publishExecSummary;
    publishToggleInFlightRef.current = true;
    setPublishExecSummary(checked);
    setPublishExecSaving(true);
    try {
      await setPublishExecutiveSummary(schoolId, checked);
      onSchoolProfilePatch?.({
        publicProfile: {
          ...(schoolProfile?.publicProfile || {}),
          publishExecutiveSummary: checked,
        },
      });
      setHubNotice({ type: 'ok', messageKey: 'exec_summary_publish_saved' });
    } catch (err) {
      console.error(err);
      setPublishExecSummary(previous);
      setHubNotice({ type: 'err', messageKey: 'exec_summary_publish_error' });
    } finally {
      publishToggleInFlightRef.current = false;
      setPublishExecSaving(false);
    }
  };

  const getExpiryDate = () => {
    const d = tsToDate(publishedAtSource());
    if (!d) return null;
    const date = new Date(d);
    date.setFullYear(date.getFullYear() + 2);
    return formatSchoolFacingDate(date, langKey);
  };

  const handleCopyEmbed = () => {
    if (!canShowBadgeCode) return;
    navigator.clipboard.writeText(buildBadgeEmbedCode(badgePublicId, selectedVariant, badgeLang));
    setHubNotice({ type: 'ok', messageKey: 'msg_copied' });
  };

  const handleDownloadCertificateForReport = async (e, archiveItem) => {
    e.stopPropagation();
    e.preventDefault();
    const reportId = archiveItem?.id || null;
    setCertBusyId(reportId);
    const pendingWindow = window.open('', '_blank');
    if (pendingWindow) {
      pendingWindow.document.write('<title>Preparing certificate...</title><p style="font-family:system-ui,sans-serif;padding:16px;">Preparing your certificate…</p>');
    }
    try {
      const fn = httpsCallable(getFunctions(), 'generateCertificatePdf');
      const res = await fn({ schoolId, archiveId: reportId });
      const url = res?.data?.url;
      if (!url) throw new Error('Certificate URL missing from server response.');
      if (pendingWindow && !pendingWindow.closed) {
        pendingWindow.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      window.setTimeout(() => {
        setHubNotice({
          type: 'ok',
          messageKey: 'hub_notice_cert_ready',
          url
        });
      }, 900);
    } catch (err) {
      if (pendingWindow && !pendingWindow.closed) pendingWindow.close();
      const detail = err?.details || '';
      const code = err?.code || '';
      const msg = err?.message || '';
      let text = detail || msg || HUB_UI_EN.btn_download;
      if (code === 'functions/internal' || /^internal$/i.test(text)) {
        text = 'Certificate generation failed on the server. Please retry in 30 seconds. If it persists, contact IFIP support.';
      }
      setHubNotice({ type: 'err', text });
    } finally {
      setCertBusyId(null);
    }
  };

  const handleDownloadPdfForReport = async (e, archiveItem) => {
    e.stopPropagation();
    e.preventDefault();
    const reportId = archiveItem?.id || null;
    setPdfBusyId(reportId);
    const pendingWindow = window.open('', '_blank');
    if (pendingWindow) {
      pendingWindow.document.write('<title>Preparing PDF...</title><p style="font-family:system-ui,sans-serif;padding:16px;">Preparing your PDF…</p>');
    }
    try {
      const fn = httpsCallable(getFunctions(), 'generateReportPdf');
      const res = await fn({ schoolId, archiveId: reportId });
      const url = res?.data?.url;
      if (!url) throw new Error('PDF URL missing from server response.');
      if (pendingWindow && !pendingWindow.closed) {
        pendingWindow.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      // Delay slightly so the message appears when the PDF viewer has opened.
      window.setTimeout(() => {
        setHubNotice({
          type: 'ok',
          text: 'Your PDF is ready. In the opened tab, click the download button to save it.',
          url
        });
      }, 900);
    } catch (err) {
      if (pendingWindow && !pendingWindow.closed) pendingWindow.close();
      const detail = err?.details || '';
      const code = err?.code || '';
      const msg = err?.message || '';
      let text = detail || msg || ui.btn_pdf_coming_soon;
      if (code === 'functions/internal' || /^internal$/i.test(text)) {
        text = 'PDF generation failed on the server. Please retry in 30 seconds. If it persists, contact IFIP support.';
      }
      setHubNotice({ type: 'err', text });
    } finally {
      setPdfBusyId(null);
    }
  };

  const publishedAtForBadge = publishedAtSource();

  useEffect(() => {
    if (!selectedArchiveReport) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedArchiveReport(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedArchiveReport]);

  if (loading) return <div className="admin-placeholder">{translatedReport?.ui?.loading_records || "Syncing..."}</div>;

  const ui = translatedReport?.ui || HUB_UI_EN;
  const publishedDateLabel = formatSchoolFacingDate(publishedAtSource(), langKey);

  const bench = benchmarkReadiness || { ready: false, minSampleSize: 100, sampleCount: 0 };
  const globalAvg = 3.0;
  const benchScore = latestArchive?.meta?.overallMaturityScore ?? report?.meta?.overallMaturityScore ?? 0;
  const benchLevel = schoolProfile?.accreditationLevel || 'foundation';
  const benchLevelDisplay = tierLabelFromUi(ui, benchLevel);
  const isNewCycleDraft = schoolProfile?.workflowStep === 'school_drafting';
  const isFeedbackUnderReview = schoolProfile?.workflowStep === 'appeals';
  const isFactCheckLocked = schoolProfile?.workflowStep === 'accepted' || acceptLockedLocally || isFeedbackUnderReview;

  const issuedReportRows =
    archive.length > 0
      ? archive
      : (legacyLiveFinal
        ? [{
          ...report,
          id: 'legacy-live',
          snapshotLabel: '',
          archivedAt: report?.meta?.publishedAt || schoolProfile?.finalReportIssuedAt
        }]
        : []);

  return (
    <div className="accreditation-hub-container">
      {hubNotice && (
        <div
          role="status"
          className={`hub-notice ${hubNotice.type === 'err' ? 'hub-notice--err' : 'hub-notice--ok'}`}
        >
          <span className="hub-notice__text">
            {(hubNotice.messageKey && ui[hubNotice.messageKey]) || hubNotice.text}
            {hubNotice.url ? (
              <>
                {' '}
                <a href={hubNotice.url} target="_blank" rel="noopener noreferrer">
                  Open PDF
                </a>
              </>
            ) : null}
          </span>
          <button
            type="button"
            onClick={() => setHubNotice(null)}
            className="hub-notice__dismiss"
          >
            {ui.btn_dismiss}
          </button>
        </div>
      )}

      <header className="hub-header">
        <h2 className="hub-header__title">{ui.hub_title}</h2>
        <p className="hub-header__subtitle">{ui.hub_subtitle}</p>
      </header>

      <div className={`hub-grid${hasCurrentCycleFinal ? ' hub-grid--finalised' : ''}`}>
        <section
          className={`hub-card active-status sef-hover-zoom-target sef-hover-zoom-target--wide${
            hasCurrentCycleFinal ? ' hub-card--status-narrow' : ''
          }`}
        >
          <div className="hub-card-header">
            <span className="hub-badge">{ui.label_current_status}</span>
          </div>
          <div className="status-display">
            <div className="status-icon">
              {isNewCycleDraft ? '↻' : hasCurrentCycleFinal ? '🏆' : schoolProfile?.workflowStep === 'fact_check' ? '📝' : '⏳'}
            </div>
            <div className="status-info">
              <h3>
                {isNewCycleDraft
                  ? (ui.status_new_cycle_title || 'New cycle open')
                  : hasCurrentCycleFinal
                  ? ui.status_published_title
                  : schoolProfile?.workflowStep === 'fact_check'
                    ? ui.status_factcheck_title
                    : ui.status_review_title}
              </h3>
              <p>
                {isNewCycleDraft
                  ? (ui.status_new_cycle_desc || 'Your previous report stays in Issued reports.')
                  : hasCurrentCycleFinal
                  ? `${ui.status_published_desc} ${publishedDateLabel}.`
                  : schoolProfile?.workflowStep === 'fact_check'
                    ? ui.status_factcheck_desc
                    : ui.status_review_desc}
              </p>
            </div>
          </div>
        </section>

        <section className="hub-card report-archive hub-card-issued-reports sef-hover-zoom-target sef-hover-zoom-target--wide">
          {hasAnyIssuedReport ? (
            <>
              <h3>{ui.header_issued_reports}</h3>
              <div className="archive-list">
                {issuedReportRows.length > 0 ? issuedReportRows.map((item) => (
                  <div key={item.id} className="archive-row-issued">
                    <div
                      role="button"
                      tabIndex={0}
                      className="archive-row-issued-main archive-meta"
                      onClick={() => setSelectedArchiveReport(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedArchiveReport(item);
                        }
                      }}
                      aria-label={`${ui.archive_row_aria_open}: ${formatIssuedReportRowTitle(item, ui, langKey, schoolProfile)}`}
                    >
                      <span className="archive-label">
                        {formatIssuedReportRowTitle(item, ui, langKey, schoolProfile)}
                      </span>
                      <span className="archive-date">
                        {formatIssueDateLine(item, ui, langKey)}
                      </span>
                    </div>
                    <div className="archive-row-actions">
                      <button
                        type="button"
                        className="btn-nudge"
                        onClick={(e) => handleDownloadCertificateForReport(e, item)}
                        disabled={certBusyId === item.id}
                        title={certBusyId === item.id ? 'Generating certificate…' : ui.btn_download}
                        aria-label={ui.btn_download}
                      >
                        {certBusyId === item.id ? 'Generating…' : ui.btn_download}
                      </button>
                      <button
                        type="button"
                        className="btn-nudge"
                        onClick={(e) => handleDownloadPdfForReport(e, item)}
                        disabled={pdfBusyId === item.id}
                        title={pdfBusyId === item.id ? 'Generating PDF…' : ui.btn_pdf_archive}
                        aria-label={ui.btn_pdf_archive}
                      >
                        {pdfBusyId === item.id ? 'Generating…' : ui.btn_pdf_archive}
                      </button>
                    </div>
                  </div>
                )) : (
                  <p className="empty-msg" style={{ opacity: 0.5, fontStyle: 'italic', fontSize: '0.9rem' }}>
                    {ui.msg_no_archive}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <h3>{ui.header_archive}</h3>
              <div className="archive-list">
                {archive.length > 0 ? archive.map((item) => (
                  <div key={item.id} className="archive-row">
                    <div className="archive-meta">
                      <span className="archive-label">{item.snapshotLabel}</span>
                      <span className="archive-date">
                        {ui.label_issued}: {formatSchoolFacingDate(item.archivedAt, langKey) || '—'}
                      </span>
                    </div>
                    <button type="button" className="btn-nudge" onClick={() => setSelectedArchiveReport(item)}>
                      {ui.btn_view_archive}
                    </button>
                  </div>
                )) : (
                  <p className="empty-msg" style={{ opacity: 0.5, fontStyle: 'italic', fontSize: '0.9rem' }}>
                    {ui.msg_no_archive}
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {hasAnyIssuedReport && (
        <section id="benchmarking-panel" className="hub-card hub-benchmark-panel sef-hover-zoom-target sef-hover-zoom-target--wide">
          <h3>{ui.benchmark_title}</h3>
          {!bench.ready ? (
            <>
              <p className="hub-glass-body">{ui.benchmark_placeholder}</p>
              <p className="hub-glass-muted">
                {(ui.benchmark_progress || '')
                  .replace('{count}', String(bench.sampleCount))
                  .replace('{threshold}', String(bench.minSampleSize))}
              </p>
            </>
          ) : (
            <p className="hub-glass-body" style={{ margin: 0 }}>
              {(ui.benchmark_ready || '')
                .replace('{global}', String(globalAvg))
                .replace('{delta}', (benchScore - globalAvg).toFixed(2))
                .replace('{level}', benchLevelDisplay)
                .replace('{count}', String(bench.sampleCount))}
            </p>
          )}
        </section>
      )}

      <section className="hub-card badge-section sef-hover-zoom-target sef-hover-zoom-target--wide">
        <div className="hub-card-header">
          <h3>{ui.badge_header}</h3>
        </div>

        <div className="hub-badge-layout">
          <div className="hub-badge-preview-col">
            <AccreditationBadge
              level={badgeTierForComponent(latestIssuedReport?.accreditationLevel || latestIssuedReport?.meta?.accreditationLevel || schoolProfile?.accreditationLevel)}
              variant={selectedVariant}
              publishedAt={publishedAtForBadge}
              langKey={badgeLang}
            />
            <div className="hub-badge-controls">
              {['medallion', 'banner', 'seal'].map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`btn-nudge hub-badge-variant-btn${selectedVariant === v ? ' hub-badge-variant-btn--active' : ''}`}
                  onClick={() => setSelectedVariant(v)}
                >
                  {ui[`badge_variant_${v}`] || v}
                </button>
              ))}

              <select
                className="hub-badge-lang-select"
                value={badgeLang || langKey || 'en'}
                onChange={(e) => setBadgeLang(e.target.value)}
              >
                {Object.entries(languageManifest)
                  .sort((a, b) => (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0]))
                  .map(([key, lang]) => (
                  <option key={key} value={key}>
                    {lang.nativeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="badge-instructions">
            <div className="hub-badge-copy-block">
              <p className="hub-glass-body hub-glass-body--lead">
                {ui.badge_desc}
              </p>
              {canShowBadgeCode && getExpiryDate() ? (
                <p className="hub-glass-body" style={{ margin: 0 }}>
                  {ui.badge_expiry_notice} <strong className="hub-glass-strong">{getExpiryDate()}</strong>.
                </p>
              ) : null}
            </div>
            <div
              className={`hub-embed-code-frame${canShowBadgeCode ? '' : ' hub-embed-code-frame--empty'}`}
              aria-label={canShowBadgeCode ? ui.badge_embed_code_label : ui.msg_pending_badge}
            >
              {canShowBadgeCode ? (
                <pre className="hub-embed-code-pre">{buildBadgeEmbedCode(badgePublicId, selectedVariant, badgeLang || langKey || 'en')}</pre>
              ) : (
                <pre className="hub-embed-code-pre hub-embed-code-pre--placeholder">{ui.msg_pending_badge}</pre>
              )}
            </div>
            {canShowBadgeCode ? (
              <button
                type="button"
                className="btn-primary-action"
                onClick={handleCopyEmbed}
              >
                {ui.btn_copy_code}
              </button>
            ) : null}
          </div>
        </div>

        {hasAnyIssuedReport ? (
          <div className="hub-exec-summary-panel">
            <h4 className="hub-exec-summary-panel__title">
              {portraitSchoolLabel(ui.exec_summary_public_title, schoolProfile?.officialName)}
            </h4>
            <p className="hub-glass-body hub-exec-summary-panel__intro">{ui.exec_summary_public_intro}</p>
            <div className="hub-exec-summary-panel__body">
              {issuedExecutiveSummary ? (
                <p className="hub-glass-body hub-exec-summary-panel__text">{issuedExecutiveSummary}</p>
              ) : (
                <p className="hub-glass-muted">{ui.exec_summary_public_empty}</p>
              )}
            </div>
            <label className="hub-exec-summary-panel__consent">
              <input
                type="checkbox"
                checked={publishExecSummary}
                disabled={!issuedExecutiveSummary}
                onChange={handlePublishExecToggle}
              />
              <span>
                {ui.exec_summary_publish_label}
                {publishExecSaving ? (
                  <span className="hub-exec-summary-panel__saving"> ({ui.exec_summary_publish_saving || 'Saving…'})</span>
                ) : null}
              </span>
            </label>
            {canShowBadgeCode ? (
              <p className="hub-exec-summary-panel__verify">
                <a
                  href={`${BADGE_PUBLIC_ORIGIN}/verify/${badgePublicId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {ui.exec_summary_preview_verify}
                </a>
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      {(schoolProfile?.workflowStep === 'fact_check' || schoolProfile?.workflowStep === 'accepted' || schoolProfile?.workflowStep === 'appeals') && report && (
        <section className="fact-check-workspace">
          <header className="workspace-header">
            <div className="workspace-title-block">
              <h3>{translatedReport?.ui?.workspace_title || "Fact-Check Workspace"}</h3>
              <p>{translatedReport?.ui?.workspace_desc || "Please review each station and provide specific feedback only if factual corrections are required."}</p>
              <p style={{ marginTop: '10px', color: 'var(--a11y-page-text)', fontSize: '0.9rem', lineHeight: 1.55, opacity: 0.9 }}>
                {ui.report_language_policy_notice || HUB_UI_EN.report_language_policy_notice}
              </p>
              <div className="hub-fc-lang-toolbar">
                <label className="hub-fc-lang-toolbar__label" htmlFor="hub-fc-report-lang-select">
                  {ui.report_language_label || 'Report language'}
                </label>
                <select
                  id="hub-fc-report-lang-select"
                  value={reportLangKey}
                  onChange={(e) => setReportLangKey(e.target.value)}
                  disabled={isFactCheckLocked}
                  className="hub-fc-lang-toolbar__select"
                >
                  {Object.entries(languageManifest)
                    .sort((a, b) => (a[1].nativeName || a[1].label || a[0]).localeCompare(b[1].nativeName || b[1].label || b[0]))
                    .map(([k, meta]) => (
                    <option key={k} value={k}>{meta.nativeName}</option>
                  ))}
                </select>
                <div
                  className="hub-fc-text-zoom-tools"
                  role="group"
                  aria-label={ui.report_text_zoom_group || HUB_UI_EN.report_text_zoom_group}
                >
                  <MagnifyIcon className="hub-fc-text-zoom-icon" />
                  <button
                    type="button"
                    className="hub-fc-text-zoom-btn"
                    disabled={isFactCheckLocked || factCheckReportTextScale <= 0.75}
                    onClick={() => setFactCheckReportTextScale((s) => clampFactCheckTextScale(s - 0.05))}
                    aria-label={ui.report_text_zoom_smaller || HUB_UI_EN.report_text_zoom_smaller}
                  >
                    −
                  </button>
                  <span className="hub-fc-text-zoom-readout" aria-live="polite">
                    {Math.round(factCheckReportTextScale * 100)}%
                  </span>
                  <button
                    type="button"
                    className="hub-fc-text-zoom-btn"
                    disabled={isFactCheckLocked || factCheckReportTextScale >= 1.35}
                    onClick={() => setFactCheckReportTextScale((s) => clampFactCheckTextScale(s + 0.05))}
                    aria-label={ui.report_text_zoom_larger || HUB_UI_EN.report_text_zoom_larger}
                  >
                    +
                  </button>
                </div>
                {isReportTranslating && (
                  <span className="hub-fc-lang-toolbar__translating">
                    {ui.report_translating_status || 'Translating report...'}
                  </span>
                )}
              </div>
              {isFactCheckLocked && (
                <p style={{ marginTop: '8px', color: 'var(--a11y-chrome)', fontWeight: 'bold' }}>
                  {isFeedbackUnderReview
                    ? (ui.workspace_under_ifip_review || "Report is now with IFIP to review factual accuracy")
                    : (ui.workspace_locked_note || HUB_UI_EN.workspace_locked_note)}
                </p>
              )}
            </div>
            <div className="workspace-actions">
              <button type="button" className="btn-secondary" onClick={handleFeedbackSubmit} disabled={isSubmittingFeedback || isFactCheckLocked}>
                {isSubmittingFeedback ? "..." : (translatedReport?.ui?.btn_submit_feedback || "Submit Accuracy Feedback")}
              </button>
              {isFeedbackUnderReview ? (
                <div
                  className="btn-primary-action"
                  style={{ width: 'auto', padding: '10px 20px', margin: 0, opacity: 0.9, cursor: 'default' }}
                >
                  {ui.workspace_under_ifip_review || "Report is now with IFIP to review factual accuracy"}
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-primary-action"
                  style={{ width: 'auto', padding: '10px 30px', margin: 0, opacity: isFactCheckLocked ? 0.8 : 1 }}
                  onClick={handleAccept}
                  disabled={isFactCheckLocked}
                >
                  {isFactCheckLocked
                    ? (translatedReport?.ui?.btn_accept_sent || HUB_UI_EN.btn_accept_sent)
                    : (translatedReport?.ui?.btn_accept || "Accept Findings")}
                </button>
              )}
            </div>
          </header>

          <div
            className="hub-fc-report-text-scale-root workspace-rows-container"
            style={{
              ['--hub-fc-text-scale']: factCheckReportTextScale,
              display: 'flex',
              flexDirection: 'column',
              background: 'color-mix(in srgb, var(--a11y-page-bg) 88%, transparent)',
              border: '1px solid var(--a11y-surface-border)',
              borderRadius: '12px',
              overflowX: 'clip',
              overflowY: 'visible',
            }}
          >
            <div className="fact-check-row fact-check-row--exec" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '40px',
              padding: '30px',
              borderBottom: '2px solid color-mix(in srgb, var(--a11y-chrome) 45%, transparent)'
            }}
            >
              <div className="report-side hub-fc-report-side">
                <h5 className="hub-fc-report-side__heading hub-fc-report-side__heading--exec">
                  {portraitSchoolLabel(ui.label_exec_summary, schoolProfile?.officialName)}
                </h5>
                <p className="hub-fc-report-side__body">
                  {translatedReport?.executiveSummary || report.institutionalBrief?.executiveSummary}
                </p>
              </div>
              <div className="feedback-side">
                <label style={{ display: 'block', marginBottom: '10px', color: 'var(--a11y-page-text)', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.8 }}>
                  {ui.label_general_notes}
                </label>
                <textarea
                  style={{
                    width: '100%', minHeight: '120px', padding: '15px', borderRadius: '6px',
                    background: 'var(--a11y-input-bg)', color: 'var(--a11y-input-text)', border: '1px solid var(--a11y-surface-border)',
                    fontSize: '0.9rem', lineHeight: '1.5'
                  }}
                  placeholder={ui.placeholder_general}
                  value={feedback.general || ""}
                  onChange={(e) => setFeedback({ ...feedback, general: e.target.value })}
                  disabled={isFactCheckLocked}
                />
              </div>
            </div>

            {[1, 2, 3, 4, 5, 6, 7].map((n) => {
              const sId = `0${n}`;
              return (
                <div key={sId} className="fact-check-row" style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '40px',
                  padding: '30px',
                  borderBottom: '1px solid var(--a11y-surface-border)',
                  background: n % 2 === 0 ? 'color-mix(in srgb, var(--a11y-surface-bg) 70%, transparent)' : 'transparent'
                }}
                >
                  <div className="report-side hub-fc-report-side">
                    <h5 className="hub-fc-report-side__heading hub-fc-report-side__heading--station">
                      {ui.label_station} {sId}: {report.stationAnalysis?.[sId]?.ifipRating} / 5
                    </h5>
                    <p className="hub-fc-report-side__body hub-fc-report-side__body--muted">
                      {translatedReport?.stationContent?.[sId] || report.stationAnalysis?.[sId]?.content}
                    </p>
                  </div>
                  <div className="feedback-side" style={{ borderLeft: '1px solid var(--a11y-surface-border)', paddingLeft: '40px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', color: 'var(--a11y-page-text)', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', opacity: 0.8 }}>
                      {ui.label_station_note}
                    </label>
                    <textarea
                      style={{
                        width: '100%', minHeight: '120px', padding: '15px', borderRadius: '6px',
                        background: 'var(--a11y-input-bg)', color: 'var(--a11y-input-text)', border: '1px solid var(--a11y-surface-border)',
                        fontSize: '0.9rem', lineHeight: '1.5'
                      }}
                      placeholder={ui.placeholder_station}
                      value={feedback[`s0${n}`] || ""}
                      onChange={(e) => setFeedback({ ...feedback, [`s0${n}`]: e.target.value })}
                      disabled={isFactCheckLocked}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {selectedArchiveReport && (
        <div
          className="archived-report-overlay__backdrop"
          onClick={() => setSelectedArchiveReport(null)}
          role="presentation"
        >
          <div
            className="archived-report-overlay__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="archived-report-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
              <div>
                <h2 id="archived-report-title" style={{ margin: 0, color: 'var(--a11y-page-text)', fontSize: '1.25rem' }}>
                  {ui.overlay_title}
                </h2>
                <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: 'var(--a11y-page-text)', opacity: 0.8 }}>
                  {formatIssuedReportRowTitle(selectedArchiveReport, ui, langKey, schoolProfile)}
                </p>
              </div>
              <button
                type="button"
                className="archived-report-overlay__close"
                onClick={() => setSelectedArchiveReport(null)}
              >
                {ui.overlay_close}
              </button>
            </div>
            <ArchivedReportView
              report={selectedArchiveReport}
              audit={audit}
              schoolProfile={schoolProfile}
              distilledData={distilledData}
              benchmarkReadiness={benchmarkReadiness}
              showBenchmarkingSection={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccreditationHub;
